import type {Element, Nodes, Root, Text} from 'hast'
import type {Schema} from 'hast-util-sanitize'
import {all} from 'lowlight'
import rehypeHighlight from 'rehype-highlight'
import type {Point} from 'unist'
import {visit} from 'unist-util-visit'
import {VFile} from 'vfile'

import {calculateEndPoint} from '../../syntax-tree/node-range-utils'
import {isPositionedNode} from '../../syntax-tree/positioned-node'
import {isTag} from '../hast-utils'
import {delimiterProperty} from './delimiters'

/**
 * Calculate and set the `position` data of all children based on their `text` contents.
 * NOTE: Assumes that the text content of the element is exactly the same as the text content of the corresponding
 * range in the source document. This is always true for code blocks.
 * @param start The starting point of the first child in the array.
 * @param children Array of nodes to assign positions to. Will recursively assign positions to these nodes' children
 * as well.
 * @returns The end point of the last child; used for recursion.
 */
function assignNodePositions(start: Point, children: Nodes[]) {
  for (const child of children) {
    if (child.type === 'text') {
      start = calculateEndPoint(start, child.value)
    } else if (child.type === 'element') {
      const end = assignNodePositions(start, child.children)
      child.position = {
        // copy objects to avoid sharing references across nodes
        start: {...start},
        end: {...end},
      }
      start = end
    }
  }
  return start
}

function isFencedCode(element: Element): boolean {
  if (!isTag(element, 'pre')) return false

  const firstChild = element.children[0]
  if (!firstChild || firstChild?.type !== 'element') return false

  const delimiterType = firstChild.properties[delimiterProperty]
  if (delimiterType !== 'codeFencedFence') return false

  return true
}

function findCodeBlockTextNode(pre: Element): Text | null {
  const code = pre.children.find(child => isTag(child, 'code'))
  if (!code) return null
  if (code.children.length !== 1) return null
  const child = code.children[0]
  return child?.type === 'text' ? child : null
}

/**
 * Replaces the contents of all language-tagged `code` element nodes with syntax-highlighted spans.
 */
export function highlightCode(tree: Root, markdownSource: string) {
  // Hast always applies a trailing newline to the text content of code nodes, even if there's no corresponding
  // newline in the source document. We need to trim this from the value to ensure that the resulting node positions
  // are valid, which in turn ensures that the CodeMirror decorations are created using valid ranges
  visit(tree, 'element', element => {
    if (!isFencedCode(element)) return

    const text = findCodeBlockTextNode(element)
    if (text) text.value = text.value.trimEnd()
  })

  const highlight = rehypeHighlight({languages: all})
  highlight(tree, new VFile(markdownSource))

  // The result from rehypeHighlight doesn't have any position data, so we need to calculate it ourselves by comparing
  // the element text contents with the source code
  visit(tree, 'element', element => {
    if (isFencedCode(element) && isPositionedNode(element)) {
      // The default start of the <code> node is covering the whole code block including the fences, but we want it to
      // only cover the code itself (the <pre> node still covers the whole block). So we start after the end of the
      // first line. No need to update the end explicitly; `calculatePositions` will do that based on the text content
      const source = markdownSource.slice(element.position.start.offset, element.position.end.offset)
      const firstLine = `${source.slice(0, source.indexOf('\n') + 1)}`

      const codeStartPoint = calculateEndPoint(element.position.start, firstLine)
      const codeNode = element.children.find(child => isTag(child, 'code'))
      if (codeNode) assignNodePositions(codeStartPoint, [codeNode])
    }
  })
}

export const highlightCodeSchema: Schema = {
  attributes: {
    span: [['className', /^hljs-/]],
  },
}
